import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Col, DatePicker, Input, Row, Select, Switch, Modal, Typography } from "antd";
import { useFormik } from "formik";
import { cloneDeep, get, toLower } from "lodash";
import Button from "../../../../components/functional_components/Button/PFButton";
import { SaveOutlined, MailOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
const { Option } = Select;
import { checkDate, checkRoles, showError, showSuccess, trimData } from "../../../../utils/utilities";
import { dbDate, langDate, projectEstimateTabs } from "../../../../utils/properties";
import Project from "../../../../services/api/Project";
import Rap from "../../../../services/api/Rap";
import { setLoading } from "../../../../redux/ducks/Loading";
import dayjs from "dayjs";

const { confirm } = Modal;
const { Text } = Typography;

const RapScreen = () => {
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const dateFormat = langDate[toLower(currentLanguage)];
  const params = useParams();
  const [state, setState] = useState({
    rapData: {},
    supportPm: [],
    pm: [],
    id: null,
    mailLoading: false,
    saveLoading: false,
    isEdit: false,
  });

  const paymentTermOptions = [
    {
      value: t("projectEstimate.detail.tabs.rap.paymentTermOptions.0"),
      id: "0",
    },
    {
      value: t("projectEstimate.detail.tabs.rap.paymentTermOptions.30"),
      id: "30",
    },
    {
      value: t("projectEstimate.detail.tabs.rap.paymentTermOptions.60"),
      id: "60",
    },
    {
      value: t("projectEstimate.detail.tabs.rap.paymentTermOptions.90"),
      id: "90",
    },
    {
      value: t("projectEstimate.detail.tabs.rap.paymentTermOptions.120"),
      id: "120",
    },
    {
      value: t("projectEstimate.detail.tabs.rap.paymentTermOptions.150"),
      id: "150",
    },
    {
      value: t("projectEstimate.detail.tabs.rap.paymentTermOptions.180"),
      id: "180",
    },
  ];

  const initiativeOptions = [
    {
      value: t("projectEstimate.detail.tabs.rap.initiativeOptions.signedContract"),
      id: "signed_contract",
    },
    {
      value: t("projectEstimate.detail.tabs.rap.initiativeOptions.waitingToBeRegularized"),
      id: "waiting_to_be_regularized",
    },
    {
      value: t("projectEstimate.detail.tabs.rap.initiativeOptions.charged"),
      id: "charged",
    },
  ];

  useEffect(() => {
    setState((prevState) => ({ ...prevState, isEdit: checkRoles(userInfo.roles, projectEstimateTabs["rap"].edit) }));
    loadInputs();
    loadRap();
  }, []);

  const loadInputs = async () => {
    dispatch(setLoading(true));
    const Pm = await Project.getPm();
    const supportPm = await Project.getSupportPm();
    if (!Pm.error && !supportPm.error) {
      setState((prevState) => ({
        ...prevState,
        supportPm: get(supportPm, "data", null),
        pm: get(Pm, "data", null),
      }));
    } else {
      if (Pm.error) {
        showError(get(Pm, "error.messageCode", "ER000"));
      }
      if (supportPm.error) {
        showError(get(supportPm, "error.messageCode", "ER000"));
      }
    }
    dispatch(setLoading(false));
  };

  const loadRap = async () => {
    dispatch(setLoading(true));
    const rap = await Rap.getRapDetail(params.id);
    if (!rap.error && rap.data) {
      setState((prevState) => ({
        ...prevState,
        rapData: get(rap, "data", {}),
        id: get(rap, "data.id", null),
      }));
    } else {
      showError(get(rap, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const saveRap = async (id, obj) => {
    setState((prevState) => ({ ...prevState, saveLoading: true }));
    const rap = await Rap.saveRap(id, obj);
    if (!rap.error && rap.data) {
      showSuccess();
      formikRap.resetForm();
      setState((prevState) => ({
        ...prevState,
        rapData: {},
      }));
      loadInputs();
      loadRap();
    } else {
      showError(get(rap, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, saveLoading: false }));
  };

  const sendMail = async () => {
    confirm({
      title: t("general.ensureSendMail"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        setState((prevState) => ({ ...prevState, mailLoading: true }));
        state.rapData.id = state.id;
        const email = await Rap.emailRap(params.id, state.rapData);
        if (!email.error) {
          loadRap();
          showSuccess();
        } else {
          showError(get(email, "error.messageCode", "ER000"));
        }
        setState((prevState) => ({ ...prevState, mailLoading: false }));
      },
      onCancel() {},
    });
  };

  const formikRap = useFormik({
    initialValues: {
      id: state.id,
      userPm: get(state.rapData, "userPm.cid", ""),
      endDate:
        get(state.rapData, "endDate", null) !== null ? dayjs(state.rapData.endDate, dbDate).format(dateFormat) : null,
      userSupportPm: get(state.rapData, "userSupportPm.cid", ""),
      inarcassa: get(state.rapData, "inarcassa", ""),
      initiative: get(state.rapData, "initiative", ""),
      paymentTerms: get(state.rapData, "paymentTerms", ""),
      projectClient: get(state.rapData, "projectClient", ""),
      projectDescription: get(state.rapData, "projectDescription", ""),
      startDate:
        get(state.rapData, "startDate", null) !== null
          ? dayjs(state.rapData.startDate, dbDate).format(dateFormat)
          : null,
      vat: get(state.rapData, "vat", ""),
      withholdingTax: get(state.rapData, "withholdingTax", ""),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.userPm) errors.userPm = "Required!";
      if (!values.userSupportPm) errors.userSupportPm = "Required!";
      if (!values.initiative) errors.initiative = "Required!";
      if (values.paymentTerms === "") errors.paymentTerms = "Required!";
      if (!values.projectClient) errors.projectClient = "Required!";
      if (!values.projectDescription) errors.projectDescription = "Required!";
      if (!values.startDate || !values.endDate || checkDate(values.startDate, values.endDate, dateFormat)) {
        errors.startDate = "Required!";
        errors.endDate = "Required!";
      }
      return errors;
    },
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      let dataValues = cloneDeep(values);
      dispatch(setLoading(true));
      dataValues.userSupportPm = state.supportPm.find((element) => dataValues.userSupportPm === element.cid);
      dataValues.userPm = state.pm.find((element) => dataValues.userPm === element.cid);
      dataValues.endDate = dataValues.endDate ? dayjs(dataValues.endDate, dateFormat).format(dbDate) : null;
      dataValues.startDate = dataValues.startDate ? dayjs(dataValues.startDate, dateFormat).format(dbDate) : null;
      dataValues.paymentTerms = parseInt(dataValues.paymentTerms);
      saveRap(params.id, dataValues);
      dispatch(setLoading(false));
    },
  });

  const checkSendRap = (isDirty, isValid) => isValid && !(isDirty && isValid);

  return (
    <>
      <Row gutter={[48, 80]}>
        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10 mb-10" htmlFor="userPm">
              {t("projectEstimate.detail.tabs.rap.userPm")}*
            </label>
          </Row>
          <Row>
            <Select
              showSearch
              placeholder={t("projectEstimate.detail.tabs.rap.userPm")}
              id="userPm"
              name="userPm"
              className="w-100"
              onBlur={formikRap.handleBlur("userPm")}
              onChange={formikRap.handleChange("userPm")}
              value={formikRap.values.userPm}
            >
              {state.pm &&
                state.pm.map((item) => (
                  <Option key={item.cid} value={item.cid} name={item.name}>
                    {item.username}
                  </Option>
                ))}
            </Select>
          </Row>
        </Col>

        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10 mb-10" htmlFor="userSupportPm">
              {t("projectEstimate.detail.tabs.rap.userSupportPm")}*
            </label>
          </Row>
          <Row>
            <Select
              showSearch
              placeholder={t("projectEstimate.detail.tabs.rap.userSupportPm")}
              id="userSupportPm"
              name="userSupportPm"
              className="w-100"
              onBlur={formikRap.handleBlur("userSupportPm")}
              onChange={formikRap.handleChange("userSupportPm")}
              value={formikRap.values.userSupportPm}
            >
              {state.supportPm &&
                state.supportPm.map((item) => (
                  <Option key={item.cid} value={item.cid} name={item.name}>
                    {item.username}
                  </Option>
                ))}
            </Select>
          </Row>
        </Col>

        <Col lg={8} md={8} xs={24} sm={10}>
          <Row className="mt-10 mb-10">
            <Col span={12}>
              <label className="fw-bold" htmlFor="projectClient">
                {t("projectEstimate.detail.tabs.rap.projectClient")}*
              </label>
            </Col>
            <Col span={12} className="right"></Col>
          </Row>
          <Row>
            <Input
              required
              id="projectClient"
              name="projectClient"
              placeholder={t("projectEstimate.detail.tabs.rap.projectClient")}
              onChange={formikRap.handleChange("projectClient")}
              onBlur={formikRap.handleBlur("projectClient")}
              value={formikRap.values.projectClient}
            />
          </Row>
        </Col>

        <Col lg={8} md={8} xs={24} sm={10}>
          <Row className="mt-10 mb-10">
            <Col span={12}>
              <label className="fw-bold" htmlFor="projectDescription">
                {t("projectEstimate.detail.tabs.rap.projectDescription")}*
              </label>
            </Col>
            <Col span={12} className="right"></Col>
          </Row>
          <Row>
            <Input
              required
              id="projectDescription"
              name="projectDescription"
              placeholder={t("projectEstimate.detail.tabs.rap.projectDescription")}
              onChange={formikRap.handleChange("projectDescription")}
              onBlur={formikRap.handleBlur("projectDescription")}
              value={formikRap.values.projectDescription}
            />
          </Row>
        </Col>

        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10 mb-10" htmlFor="initiative">
              {t("projectEstimate.detail.tabs.rap.initiative")}*
            </label>
          </Row>
          <Row>
            <Select
              showSearch
              placeholder={t("projectEstimate.detail.tabs.rap.initiative")}
              id="initiative"
              name="initiative"
              className="w-100"
              onBlur={formikRap.handleBlur("initiative")}
              onChange={formikRap.handleChange("initiative")}
              value={formikRap.values.initiative}
            >
              {initiativeOptions.map((item) => (
                <Option key={item.id} value={item.id} name={item.value}>
                  {item.value}
                </Option>
              ))}
            </Select>
          </Row>
        </Col>

        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10 mb-10" htmlFor="paymentTerms">
              {t("projectEstimate.detail.tabs.rap.paymentTerms")}*
            </label>
          </Row>
          <Row>
            <Select
              showSearch
              placeholder={t("projectEstimate.detail.tabs.rap.paymentTerms")}
              id="paymentTerms"
              name="paymentTerms"
              className="w-100"
              onBlur={formikRap.handleBlur("paymentTerms")}
              onChange={formikRap.handleChange("paymentTerms")}
              value={formikRap.values.paymentTerms}
            >
              {paymentTermOptions.map((item) => (
                <Option key={item.id} value={item.id} name={item.id}>
                  {item.value}
                </Option>
              ))}
            </Select>
          </Row>
        </Col>

        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="dateActivityStart">
              {t("projectEstimate.detail.tabs.rap.dateActivityStart")}*
            </label>
          </Row>
          <Row>
            <DatePicker
              id="dateActivityStart"
              name="dateActivityStart"
              format={dateFormat}
              locale={langDate[`locale_${toLower(currentLanguage)}`]}
              value={formikRap.values.startDate ? dayjs(formikRap.values.startDate, dateFormat) : undefined}
              onChange={(date, dateString) => {
                formikRap.setFieldValue("startDate", dateString);
              }}
            />
          </Row>
        </Col>

        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="dateActivityEnd">
              {t("projectEstimate.detail.tabs.rap.dateActivityEnd")}*
            </label>
          </Row>
          <Row>
            <DatePicker
              id="dateActivityEnd"
              name="dateActivityEnd"
              format={dateFormat}
              locale={langDate[`locale_${toLower(currentLanguage)}`]}
              value={formikRap.values.endDate ? dayjs(formikRap.values.endDate, dateFormat) : undefined}
              onChange={(date, dateString) => {
                formikRap.setFieldValue("endDate", dateString);
              }}
            />
          </Row>
        </Col>

        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="withholdingTax">
              {t("projectEstimate.detail.tabs.rap.withholdingTax")}
            </label>
          </Row>
          <Row>
            <Switch
              id="withholdingTax"
              name="withholdingTax"
              onChange={() => {
                formikRap.setFieldValue("withholdingTax", !formikRap.values.withholdingTax);
              }}
              checked={formikRap.values.withholdingTax}
            />
          </Row>
        </Col>

        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="vat">
              {t("projectEstimate.detail.tabs.rap.vat")}
            </label>
          </Row>
          <Row>
            <Switch
              id="vat"
              name="vat"
              onChange={() => {
                formikRap.setFieldValue("vat", !formikRap.values.vat);
              }}
              checked={formikRap.values.vat}
            />
          </Row>
        </Col>

        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="inarcassa">
              {t("projectEstimate.detail.tabs.rap.inarcassa")}
            </label>
          </Row>
          <Row>
            <Switch
              id="inarcassa"
              name="inarcassa"
              onChange={() => {
                formikRap.setFieldValue("inarcassa", !formikRap.values.inarcassa);
              }}
              checked={formikRap.values.inarcassa}
            />
          </Row>
        </Col>
      </Row>
      <Row justify="end" align="middle" className="mb-1em mt-1em">
        <Col className="mr-10px">
          <Row>
            <div className="boxValue right">
              <Text>
                {t("projectEstimate.detail.tabs.rap.lastSendDate")}:{" "}
                {state.rapData.lastSendDate
                  ? dayjs(state.rapData.lastSendDate, dbDate).format(dateFormat)
                  : t("projectEstimate.detail.tabs.rap.noLastSendDate")}
              </Text>
            </div>
          </Row>
        </Col>
        <Col>
          <Row justify="end">
            <Button
              isLoading={state.loading}
              currentIcon={<SaveOutlined className="icon-medium" />}
              clickCallback={formikRap.submitForm}
              isDisabled={!(formikRap.dirty && formikRap.isValid) || !state.isEdit}
            />
            <Button
              className="ml-10"
              isLoading={state.mailLoading}
              currentIcon={<MailOutlined className="icon-medium" />}
              clickCallback={sendMail}
              isDisabled={!checkSendRap(formikRap.dirty, formikRap.isValid) || !state.isEdit}
            />
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default RapScreen;
